// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';

/**
 * Advisor (read-only) access to the Financial Position record layer projected
 * from a relationship's Fact-Find. Mirrors the Resource Server's
 * /api/profiles/{profileId}/{financial-position,accounts} surface (proxied by
 * advisor-bff; X-Brokerage-Id injected by the advisorApi interceptor).
 * See docs/specs/financial-position-prd.md §9.
 */

export interface FinancialPosition {
  totalAssets: number;
  totalLiabilities: number;
  netWorth: number;
  currency: string | null;
  accountCount: number;
  assetCount: number;
  monthlyIncome: number | null;
  monthlyExpenditure: number | null;
  monthlySurplus: number | null;
}

export interface CashflowItemSummary {
  id: string;
  ownerClientId: string | null;
  isJoint: boolean;
  direction: 'INCOME' | 'EXPENDITURE';
  category: string;
  amount: number;
  frequency: string;
  currencyCode: string | null;
  monthlyAmount: number | null;
  bonusAmount: number | null;
  shareOptionsAmount: number | null;
  source: string;
  factFindLinked: boolean;
}

export interface AssetSummary {
  id: string;
  ownerClientId: string | null;
  isJoint: boolean;
  assetType: string;
  currentValue: number | null;
  currencyCode: string | null;
  rentalIncomeAmount: number | null;
  rentalIncomeFrequency: string | null;
  maintenanceCostAmount: number | null;
  mortgageBalance: number | null;
  equity: number | null;
  source: string;
  factFindLinked: boolean;
}

export interface AccountSummary {
  id: string;
  ownerClientId: string | null;
  isJoint: boolean;
  accountType: string;
  classification: 'ASSET' | 'LIABILITY';
  balance: number | null;
  currencyCode: string | null;
  institutionName: string | null;
  iban: string | null;
  interestRate: number | null;
  interestType: string | null;
  termRemainingMonths: number | null;
  monthlyRepayment: number | null;
  securedAssetId: string | null;
  source: string;
  factFindLinked: boolean;
  /** True when this account is the advisor-designated surplus-sweep target. */
  sweepTarget?: boolean;
}

// ── Tax classification (cashflow-tax-modeling-prd.md §7.7) ──

/** A taxable item (asset / investment holding / business holding) with its tax treatment. */
export interface TaxClassificationItem {
  kind: 'ASSET' | 'INVESTMENT' | 'HOLDING';
  id: string;
  label: string;
  owner: string;
  itemType: string;
  /** The treatment in effect — the override when set, else the derived default. */
  effectiveTreatment: string;
  /** True when an advisor override is in place (else the derived default). */
  overridden: boolean;
  /** ASSET-only flag (read-only in v1); null for non-asset kinds. */
  principalPrivateResidence: boolean | null;
}

/** Per-client household tax-profile flags driving credits/exemptions. */
export interface TaxClassificationClientFlags {
  clientId: string;
  name: string;
  medicalCard: boolean;
  selfEmployed: boolean;
  homeCarer: boolean;
  singlePersonChildCarer: boolean;
  incapacitatedChildrenCount: number;
  dependentRelativesCount: number;
}

export interface TaxClassification {
  items: TaxClassificationItem[];
  clients: TaxClassificationClientFlags[];
}

/** Maps an item kind to its tax-treatment write endpoint segment. */
function taxSegmentForKind(kind: TaxClassificationItem['kind']): string {
  switch (kind) {
    case 'ASSET':
      return 'assets';
    case 'INVESTMENT':
      return 'investments';
    case 'HOLDING':
      return 'holdings';
  }
}

// ── Anticipated inheritances (CAT) + corporation tax (cashflow-tax-modeling-prd.md §7.3/§7.4) ──

/** CAT group for an anticipated inheritance. */
export type CatGroup = 'A' | 'B' | 'C';

/** An anticipated inheritance for Capital Acquisitions Tax (CAT) planning. */
export interface AnticipatedInheritance {
  id: string | null;
  ownerClientId: string | null;
  sourceRelationship: string | null;
  catGroup: CatGroup;
  expectedValue: number | null;
  expectedYear: number | null;
  priorAggregateInGroup: number;
  isBusinessAsset: boolean;
  isAgricultural: boolean;
  smallGiftApplicable: boolean;
}

/** An advisor-managed financial goal (gap A5 — replaces the orphaned Fact-Find goals capture). */
export interface FinancialGoal {
  id: string | null;
  title: string | null;
  goalType: string | null;
  priority: string | null;
  targetAmount: number | null;
  targetDate: string | null;
  status: string | null;
}

/** A read-only corporation-tax line derived from a captured business interest's P&L. */
export interface CorporationTax {
  businessInterestId: string;
  interestType: string;
  tradingIncome: number | null;
  passiveIncome: number | null;
  corporationTax: number;
  closeCompanySurcharge: number;
  total: number;
}

export const financialPositionApi = {
  // ── Financial goals (advisor-managed) ──

  listGoals: async (profileId: string): Promise<FinancialGoal[]> => {
    const { data } = await api.get<FinancialGoal[]>(`/profiles/${profileId}/financial-goals`);
    return data;
  },

  createGoal: async (profileId: string, body: FinancialGoal): Promise<FinancialGoal> => {
    const { data } = await api.post<FinancialGoal>(`/profiles/${profileId}/financial-goals`, body);
    return data;
  },

  updateGoal: async (profileId: string, id: string, body: FinancialGoal): Promise<FinancialGoal> => {
    const { data } = await api.put<FinancialGoal>(
      `/profiles/${profileId}/financial-goals/${id}`,
      body,
    );
    return data;
  },

  deleteGoal: async (profileId: string, id: string): Promise<void> => {
    await api.delete(`/profiles/${profileId}/financial-goals/${id}`);
  },

  // ── Anticipated inheritances (CAT) ──

  /** List the relationship's anticipated inheritances. */
  listInheritances: async (profileId: string): Promise<AnticipatedInheritance[]> => {
    const { data } = await api.get<AnticipatedInheritance[]>(
      `/profiles/${profileId}/financial-position/inheritances`,
    );
    return data;
  },

  /** Create an anticipated inheritance (id is server-assigned). */
  createInheritance: async (
    profileId: string,
    body: AnticipatedInheritance,
  ): Promise<AnticipatedInheritance> => {
    const { data } = await api.post<AnticipatedInheritance>(
      `/profiles/${profileId}/financial-position/inheritances`,
      body,
    );
    return data;
  },

  /** Update an existing anticipated inheritance. */
  updateInheritance: async (
    profileId: string,
    id: string,
    body: AnticipatedInheritance,
  ): Promise<AnticipatedInheritance> => {
    const { data } = await api.put<AnticipatedInheritance>(
      `/profiles/${profileId}/financial-position/inheritances/${id}`,
      body,
    );
    return data;
  },

  /** Delete an anticipated inheritance (204 no content). */
  deleteInheritance: async (profileId: string, id: string): Promise<void> => {
    await api.delete(`/profiles/${profileId}/financial-position/inheritances/${id}`);
  },

  /** Read-only corporation-tax lines for the relationship's company P&Ls (optionally for a year). */
  getCorporationTax: async (profileId: string, year?: number): Promise<CorporationTax[]> => {
    const { data } = await api.get<CorporationTax[]>(
      `/profiles/${profileId}/financial-position/corporation-tax`,
      { params: year != null ? { year } : undefined },
    );
    return data;
  },

  /** Read the relationship's taxable items + household tax-profile flags. */
  getTaxClassification: async (profileId: string): Promise<TaxClassification> => {
    const { data } = await api.get<TaxClassification>(
      `/profiles/${profileId}/financial-position/tax-classification`,
    );
    return data;
  },

  /**
   * Set (or clear) an item's tax treatment. The {@code kind} selects the
   * endpoint segment (ASSET→assets, INVESTMENT→investments, HOLDING→holdings);
   * a {@code null} treatment clears the override (reverts to the derived
   * default). Returns the effective treatment string.
   */
  setTaxTreatment: async (
    profileId: string,
    kind: TaxClassificationItem['kind'],
    id: string,
    treatment: string | null,
  ): Promise<string> => {
    const { data } = await api.put<string>(
      `/profiles/${profileId}/financial-position/${taxSegmentForKind(kind)}/${id}/tax-treatment`,
      { treatment },
    );
    return data;
  },

  /** Set a client's household tax-profile flags (204 no content). */
  setClientTaxProfile: async (
    profileId: string,
    clientId: string,
    flags: {
      medicalCard: boolean;
      selfEmployed: boolean;
      homeCarer: boolean;
      singlePersonChildCarer: boolean;
      incapacitatedChildrenCount: number;
      dependentRelativesCount: number;
    },
  ): Promise<void> => {
    await api.put(`/profiles/${profileId}/financial-position/clients/${clientId}/tax-profile`, flags);
  },

  getPosition: async (profileId: string): Promise<FinancialPosition> => {
    const { data } = await api.get<FinancialPosition>(`/profiles/${profileId}/financial-position`);
    return data;
  },

  listAccounts: async (
    profileId: string,
    classification?: 'ASSET' | 'LIABILITY',
  ): Promise<AccountSummary[]> => {
    const { data } = await api.get<AccountSummary[]>(`/profiles/${profileId}/accounts`, {
      params: classification ? { classification } : undefined,
    });
    return data;
  },

  /** Designate the surplus-sweep target account (positive/ASSET accounts only). */
  setSweepAccount: async (profileId: string, accountId: string): Promise<AccountSummary> => {
    const { data } = await api.put<AccountSummary>(
      `/profiles/${profileId}/financial-position/accounts/${accountId}/sweep`,
    );
    return data;
  },

  listAssets: async (profileId: string): Promise<AssetSummary[]> => {
    const { data } = await api.get<AssetSummary[]>(`/profiles/${profileId}/assets`);
    return data;
  },

  listCashflowItems: async (
    profileId: string,
    direction?: 'INCOME' | 'EXPENDITURE',
  ): Promise<CashflowItemSummary[]> => {
    const { data } = await api.get<CashflowItemSummary[]>(`/profiles/${profileId}/cashflow-items`, {
      params: direction ? { direction } : undefined,
    });
    return data;
  },

  getDashboard: async (profileId: string): Promise<FinancialDashboard> => {
    const { data } = await api.get<FinancialDashboard>(
      `/profiles/${profileId}/financial-position/dashboard`,
    );
    return data;
  },

  /**
   * Future-Values projection for a single account (year-by-year value).
   * Resolves to null when the account is not projectable (HTTP 204).
   */
  getAccountProjection: async (
    profileId: string,
    accountId: string,
  ): Promise<FinancialProjection | null> => {
    const res = await api.get<FinancialProjection>(
      `/profiles/${profileId}/financial-position/accounts/${accountId}/projection`,
    );
    return res.status === 204 ? null : res.data;
  },

  /**
   * Future-Values projection for a pension/investment policy, holding, or
   * property/collectable asset (null when not projectable). The {@code kind}
   * is the endpoint segment; {@code 'assets'} covers PROPERTY + COLLECTIBLE.
   */
  getPolicyProjection: async (
    profileId: string,
    kind: ProjectionKind,
    policyId: string,
  ): Promise<FinancialProjection | null> => {
    const res = await api.get<FinancialProjection>(
      `/profiles/${profileId}/financial-position/${kind}/${policyId}/projection`,
    );
    return res.status === 204 ? null : res.data;
  },

  /** Set the advisor growth rate (%) on a policy/holding/asset; returns the recomputed projection (null when none). */
  setPolicyGrowthRate: async (
    profileId: string,
    kind: ProjectionKind,
    policyId: string,
    rate: number | null,
  ): Promise<FinancialProjection | null> => {
    const res = await api.put<FinancialProjection>(
      `/profiles/${profileId}/financial-position/${kind}/${policyId}/growth-rate`,
      { rate },
    );
    return res.status === 204 ? null : res.data;
  },

  /**
   * Reconcile an income item's gross/net basis (figure provenance). statedNetAmount
   * is annual; required for NET / GROSS_AND_NET.
   */
  reconcileIncome: async (
    profileId: string,
    itemId: string,
    body: { entryBasis: 'GROSS' | 'NET' | 'GROSS_AND_NET'; statedNetAmount: number | null; adjustmentReason: string | null },
  ): Promise<void> => {
    await api.patch(`/profiles/${profileId}/financial-position/income/${itemId}/reconcile`, body);
  },

  /** Selectable income + expenditure figures (key + label + kind) for scenario overrides. */
  getFigures: async (profileId: string): Promise<FigureOption[]> => {
    const { data } = await api.get<FigureOption[]>(`/profiles/${profileId}/financial-position/figures`);
    return data;
  },

  /** Versions on an income figure's timeline (figure provenance — temporal). */
  getIncomeTimeline: async (profileId: string, itemId: string): Promise<FigureVersion[]> => {
    const { data } = await api.get<FigureVersion[]>(
      `/profiles/${profileId}/financial-position/income/${itemId}/timeline`,
    );
    return data;
  },

  addIncomeVersion: async (
    profileId: string,
    itemId: string,
    body: { validFrom: string; amount: number; frequency: string | null },
  ): Promise<FigureVersion> => {
    const { data } = await api.post<FigureVersion>(
      `/profiles/${profileId}/financial-position/income/${itemId}/versions`,
      body,
    );
    return data;
  },

  deleteIncomeVersion: async (profileId: string, versionId: string): Promise<void> => {
    await api.delete(`/profiles/${profileId}/financial-position/income/versions/${versionId}`);
  },
};

/** A selectable figure for scenario overrides (cashflow figure or pension/investment policy). */
export interface FigureOption {
  key: string;
  label: string;
  kind: 'INCOME' | 'EXPENDITURE' | 'PENSION' | 'INVESTMENT';
  /** The base value that fed the calc before any override (Phase 6). */
  baseValue: number | null;
  /** Frequency of `baseValue` for income/spend; null for policy values. */
  frequency: string | null;
}

/** One version on a figure's timeline. */
export interface FigureVersion {
  id: string;
  validFrom: string;
  amount: number;
  frequency: string;
  source: string | null;
  base: boolean;
}

/** Endpoint segment for a per-item Future-Values projection / growth-rate. */
export type ProjectionKind = 'pensions' | 'investments' | 'investment-holdings' | 'assets';

// ── Overview dashboard aggregation (see financial-position-dashboard-prd.md §7) ──

export interface DashClientRef {
  id: string;
  name: string;
}
export interface DashAccountRow {
  accountId: string;
  label: string;
  type: string;
  classification: 'ASSET' | 'LIABILITY';
  owner: string;
  amount: number;
  /** 'UP' | 'DOWN' when the account is projectable; null otherwise (no Future-Values icon). */
  projectionDirection: string | null;
  /** True when the balance was estimated from the repayment schedule (no balance entered). */
  balanceEstimated: boolean;
}

export interface FinancialProjectionPoint {
  year: number;
  value: number;
}
/** A named line for a multi-series projection (e.g. Value / Mortgage / Equity). */
export interface FinancialNamedSeries {
  key: string;
  label: string;
  series: FinancialProjectionPoint[];
}
export interface FinancialProjection {
  itemId: string;
  kind: string;
  label: string;
  direction: 'UP' | 'DOWN' | 'FLAT';
  basis: string;
  currency: string | null;
  horizonYears: number;
  series: FinancialProjectionPoint[];
  /**
   * Optional multi-line breakdown aligned to `series` by year — populated for a
   * property with a secured mortgage (Value / Mortgage / Equity). When present the
   * modal plots these lines instead of the single `series`.
   */
  breakdown?: FinancialNamedSeries[] | null;
}
export interface DashDonutSlice {
  label: string;
  amount: number;
}
export interface DashIncomeRow {
  itemId: string;
  category: string;
  label: string;
  owner: string;
  annual: number;
  /** Apportioned annual tax for this income (null when the tax layer is off). */
  tax: number | null;
  /** Tax split by component (Income tax / USC / PRSI / DIRT / Total); null when tax off. */
  taxByComponent: Record<string, number> | null;
  /** Figure provenance (null when the figure-provenance flag is off). */
  grossSource: 'PROVIDED' | 'CALCULATED' | 'RECONCILED' | null;
  netSource: 'PROVIDED' | 'CALCULATED' | 'RECONCILED' | null;
  netProvided: number | null;
  netCalculated: number | null;
  /** provided − calculated net (only on a RECONCILED net). */
  netDiscrepancy: number | null;
  netWithinTolerance: boolean | null;
  /** Stable figure id — the scenario override target ("figure key"); null for manual items. */
  projectionKey?: string | null;
}
export interface DashExpenditureRow {
  category: string;
  label: string;
  annual: number;
}
export interface DashOwnerValue {
  owner: string;
  value: number;
  policyCount: number | null;
}
export interface DashPolicyValue {
  policyId: string;
  label: string;
  owner: string;
  value: number;
  /** 'UP' | 'FLAT' when projectable; null otherwise. */
  projectionDirection: string | null;
  /** Endpoint segment for the projection: 'pensions' | 'investments' | 'investment-holdings' | 'assets'. */
  projectionKind: ProjectionKind;
}
export interface DashPropertyRow {
  assetId: string;
  label: string;
  owner: string;
  value: number;
  mortgage: number | null;
  equity: number;
  /** 'UP' | 'FLAT' when the property value is projectable; null otherwise. */
  projectionDirection: string | null;
}

export interface FinancialDashboard {
  currency: string | null;
  clients: DashClientRef[];
  accountBalances: DashAccountRow[];
  income: { rows: DashIncomeRow[]; donut: DashDonutSlice[]; totalAnnual: number };
  expenditure: {
    debtAnnual: number;
    expensesAnnual: number;
    rows: DashExpenditureRow[];
    donut: DashDonutSlice[];
    totalAnnual: number;
  };
  pensions: { perOwner: DashOwnerValue[]; policies: DashPolicyValue[]; total: number; policyCount: number | null };
  investments: { perOwner: DashOwnerValue[]; policies: DashPolicyValue[]; total: number; policyCount: number | null };
  protection: {
    total: number;
    lifeCover: number;
    incomeProtection: number;
    illnessBenefit: number;
  };
  property: {
    rows: DashPropertyRow[];
    totalValue: number;
    totalMortgage: number;
    totalEquity: number;
  };
}
